import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
    ShieldCheck, AlertTriangle, ChevronDown, ChevronRight,
    Download, RefreshCw, Copy, CheckCircle2,
} from "lucide-react";

/**
 * Iter77 — Admin Audit Log Viewer (kaafi_system_admin only).
 *
 * Panels:
 *   1. Chain-integrity widget with manual "Verify chain now".
 *   2. Filter card — date range / actor / action / entity / module / ip / keyword.
 *   3. Table — seq, at, actor, action, entity, module, diff chips.
 *   4. Row expand — full before/after/diff JSON + prev_hash + hash + copy.
 *   5. Export button — CSV / JSON of current filter.
 */

const MODULES = ["ALL", "AP", "IC", "CN", "GLOBAL"];

function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return String(s); }
}

function DiffChips({ diff }) {
    if (!diff || !diff.changed) return <span className="text-slate-400">—</span>;
    const entries = Object.entries(diff.changed);
    if (entries.length === 0) return <span className="text-slate-400">—</span>;
    return (
        <div className="flex gap-1 flex-wrap">
            {entries.slice(0, 3).map(([k, [oldV, newV]]) => (
                <span key={k} className="inline-block text-[11px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono">
                    <span className="text-slate-500">{k}: </span>
                    <span className="text-rose-600">{JSON.stringify(oldV).slice(0, 20)}</span>
                    <span className="text-slate-400"> → </span>
                    <span className="text-emerald-600">{JSON.stringify(newV).slice(0, 20)}</span>
                </span>
            ))}
            {entries.length > 3 && (
                <span className="text-[11px] text-slate-500">+{entries.length - 3} more</span>
            )}
        </div>
    );
}

function ChainIntegrity({ onQuarantinedSeqs }) {
    const [state, setState] = useState({
        status: "loading", verifiedAt: null, verifiedCount: 0,
        tamperAt: null, acknowledgedSeqs: [], quarantinedSeqs: [],
    });
    const [busy, setBusy] = useState(false);

    const verify = async () => {
        setBusy(true);
        try {
            const r = await api.get("/admin/audit/verify-chain");
            const ack = r.data.acknowledged_seqs || [];
            const quarantined = r.data.quarantined_seqs || [];
            const isOkChain = !!r.data.ok;
            setState({
                status: isOkChain ? "ok" : "tamper",
                verifiedAt: new Date().toISOString(),
                verifiedCount: r.data.verified_count,
                tamperAt: r.data.tamper_at,
                acknowledgedSeqs: ack,
                quarantinedSeqs: quarantined,
            });
            if (onQuarantinedSeqs) onQuarantinedSeqs(quarantined);
            if (isOkChain) {
                if (ack.length > 0) {
                    toast.success(`Chain OK — ${r.data.verified_count} entries walked · ${ack.length} quarantined`);
                } else {
                    toast.success(`Chain OK — ${r.data.verified_count} entries walked`);
                }
            } else {
                toast.error(`Tamper detected at seq #${r.data.tamper_at}`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    useEffect(() => { verify(); /* eslint-disable-next-line */ }, []);

    const isOk = state.status === "ok";
    const isTamper = state.status === "tamper";
    const hasQuarantined = isOk && (state.acknowledgedSeqs?.length || 0) > 0;

    return (
        <Card
            className={isOk ? "border-emerald-200 bg-emerald-50" : isTamper ? "border-rose-200 bg-rose-50" : ""}
            data-testid="chain-integrity-widget"
        >
            <CardContent className="p-4 flex items-center gap-4">
                {isOk && !hasQuarantined && <ShieldCheck className="w-6 h-6 text-emerald-600" data-testid="chain-ok" />}
                {isOk && hasQuarantined && (
                    <span className="relative inline-flex" data-testid="chain-ok-quarantined">
                        <ShieldCheck className="w-6 h-6 text-emerald-600" />
                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white" />
                    </span>
                )}
                {isTamper && <AlertTriangle className="w-6 h-6 text-rose-600" data-testid="chain-tamper" />}
                {state.status === "loading" && <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />}
                <div className="flex-1">
                    <div className="text-sm font-semibold">
                        {isOk && !hasQuarantined && <span className="text-emerald-800">Chain OK</span>}
                        {isOk && hasQuarantined && (
                            <span className="text-emerald-800">
                                Chain OK · {state.acknowledgedSeqs.length} quarantined entr{state.acknowledgedSeqs.length === 1 ? "y" : "ies"} acknowledged
                            </span>
                        )}
                        {isTamper && <span className="text-rose-800">Tamper detected at seq #{state.tamperAt}</span>}
                        {state.status === "loading" && <span className="text-slate-600">Verifying chain…</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                        {state.verifiedAt && <>Last verified {fmtDate(state.verifiedAt)} · {state.verifiedCount} entries walked</>}
                        {hasQuarantined && (
                            <span className="ml-1 text-amber-700">
                                (seq{state.acknowledgedSeqs.length === 1 ? "" : "s"} {state.acknowledgedSeqs.join(", ")})
                            </span>
                        )}
                    </div>
                </div>
                <Button size="sm" onClick={verify} disabled={busy} data-testid="verify-chain-now">
                    {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : "Verify chain now"}
                </Button>
            </CardContent>
        </Card>
    );
}

function RowDetail({ entry }) {
    const copy = (obj) => {
        try {
            navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
            toast.success("Copied to clipboard");
        } catch { toast.error("Copy failed"); }
    };
    return (
        <tr>
            <td colSpan={7} className="bg-slate-50 p-4">
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center justify-between mb-2">
                            <span>Before</span>
                            <Copy className="w-3 h-3 cursor-pointer" onClick={() => copy(entry.before)} />
                        </div>
                        <pre className="text-[11px] bg-white border rounded p-2 max-h-64 overflow-auto font-mono">
                            {JSON.stringify(entry.before, null, 2)}
                        </pre>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center justify-between mb-2">
                            <span>After</span>
                            <Copy className="w-3 h-3 cursor-pointer" onClick={() => copy(entry.after)} />
                        </div>
                        <pre className="text-[11px] bg-white border rounded p-2 max-h-64 overflow-auto font-mono">
                            {JSON.stringify(entry.after, null, 2)}
                        </pre>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center justify-between mb-2">
                            <span>Diff</span>
                            <Copy className="w-3 h-3 cursor-pointer" onClick={() => copy(entry.diff)} />
                        </div>
                        <pre className="text-[11px] bg-white border rounded p-2 max-h-64 overflow-auto font-mono">
                            {JSON.stringify(entry.diff, null, 2)}
                        </pre>
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-[11px] font-mono text-slate-500">
                    <div><span className="text-slate-400">prev_hash:</span> {entry.prev_hash}</div>
                    <div><span className="text-slate-400">hash:</span> {entry.hash}</div>
                    {entry.reason && <div><span className="text-slate-400">reason:</span> {entry.reason}</div>}
                </div>
            </td>
        </tr>
    );
}

export default function AdminAudit() {
    const [rows, setRows] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [actions, setActions] = useState([]);
    const [quarantinedSeqs, setQuarantinedSeqs] = useState([]);
    const [filters, setFilters] = useState({
        from: "", to: "", actor: "", action: "",
        entity_type: "", entity_id: "", module: "", ip: "", q: "",
    });
    const quarantinedSet = useMemo(() => new Set(quarantinedSeqs), [quarantinedSeqs]);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/admin/audit/actions");
                setActions(r.data?.items || []);
            } catch { /* silent — dropdown will fall back to empty */ }
        })();
    }, []);

    const buildQuery = (cursor) => {
        const qp = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v) qp.append(k, v);
        });
        qp.append("limit", "50");
        if (cursor) qp.append("cursor", cursor);
        return qp.toString();
    };

    const load = async ({ append = false } = {}) => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/audit?${buildQuery(append ? nextCursor : null)}`);
            setRows(append ? [...rows, ...(r.data?.items || [])] : (r.data?.items || []));
            setNextCursor(r.data?.next_cursor || null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        /* eslint-disable-next-line */
    }, [filters.from, filters.to, filters.actor, filters.action,
        filters.entity_type, filters.module, filters.ip, filters.q]);

    const exportCurrent = async (fmt) => {
        try {
            const body = { ...filters, format: fmt, limit: 5000 };
            // Rename `from` → `frm` to avoid reserved-name clashes on the backend.
            body.frm = body.from; delete body.from;
            const r = await api.post("/admin/audit/export", body, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `audit_log_${new Date().toISOString().slice(0, 19)}.${fmt}`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`Exported ${fmt.toUpperCase()}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Audit Log">
            <div className="space-y-4" data-testid="admin-audit-page">
                <ChainIntegrity onQuarantinedSeqs={setQuarantinedSeqs} />

                <Card>
                    <CardHeader><CardTitle className="text-base">Filters</CardTitle></CardHeader>
                    <CardContent className="grid md:grid-cols-4 gap-3">
                        <div>
                            <Label className="text-xs">From</Label>
                            <Input type="datetime-local" value={filters.from}
                                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                                data-testid="filter-from" />
                        </div>
                        <div>
                            <Label className="text-xs">To</Label>
                            <Input type="datetime-local" value={filters.to}
                                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                                data-testid="filter-to" />
                        </div>
                        <div>
                            <Label className="text-xs">Actor (email)</Label>
                            <Input value={filters.actor}
                                onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
                                placeholder="hr@…" data-testid="filter-actor" />
                        </div>
                        <div>
                            <Label className="text-xs">Action</Label>
                            <Select value={filters.action || "all"} onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}>
                                <SelectTrigger data-testid="filter-action"><SelectValue placeholder="All actions" /></SelectTrigger>
                                <SelectContent className="max-h-64">
                                    <SelectItem value="all">All actions</SelectItem>
                                    {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Entity type</Label>
                            <Input value={filters.entity_type}
                                onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                                placeholder="employee, payroll_line…" data-testid="filter-entity-type" />
                        </div>
                        <div>
                            <Label className="text-xs">Entity ID</Label>
                            <Input value={filters.entity_id}
                                onChange={(e) => setFilters({ ...filters, entity_id: e.target.value })}
                                data-testid="filter-entity-id" />
                        </div>
                        <div>
                            <Label className="text-xs">Module</Label>
                            <Select value={filters.module || "all"} onValueChange={(v) => setFilters({ ...filters, module: v === "all" ? "" : v })}>
                                <SelectTrigger data-testid="filter-module"><SelectValue placeholder="All" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    {MODULES.slice(1).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Keyword</Label>
                            <Input value={filters.q}
                                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                                placeholder="reason / entity id" data-testid="filter-q" />
                        </div>
                        <div className="md:col-span-4 flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => exportCurrent("csv")} data-testid="export-csv">
                                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => exportCurrent("json")} data-testid="export-json">
                                <Download className="w-3.5 h-3.5 mr-1.5" /> JSON
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card data-testid="audit-table-card">
                    <CardContent className="p-0">
                        {loading && rows.length === 0 ? <div className="p-6 text-slate-500"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                        : rows.length === 0 ? <div className="p-6 text-slate-500 text-sm" data-testid="audit-empty">No entries match the current filters.</div>
                        : (
                            <div className="overflow-auto max-h-[70vh]">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                                        <tr>
                                            <th className="text-right px-3 py-2 w-16">Seq</th>
                                            <th className="text-left px-3 py-2">When</th>
                                            <th className="text-left px-3 py-2">Actor</th>
                                            <th className="text-left px-3 py-2">Action</th>
                                            <th className="text-left px-3 py-2">Entity · Module</th>
                                            <th className="text-left px-3 py-2">Diff</th>
                                            <th className="text-left px-3 py-2 w-8"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <React.Fragment key={r.seq}>
                                                <tr
                                                    className="border-t hover:bg-slate-50 cursor-pointer"
                                                    onClick={() => setExpanded(expanded === r.seq ? null : r.seq)}
                                                    data-testid={`audit-row-${r.seq}`}
                                                >
                                                    <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">
                                                        {r.seq}
                                                        {quarantinedSet.has(r.seq) && (
                                                            <span
                                                                className="ml-1 inline-block text-[9px] px-1 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-medium align-middle"
                                                                title="This entry is quarantined by a chain-restart marker"
                                                                data-testid={`audit-row-${r.seq}-quarantined`}
                                                            >
                                                                Q
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmtDate(r.at)}</td>
                                                    <td className="px-3 py-2">
                                                        <div className="text-slate-700">{r.actor_email || "—"}</div>
                                                        {r.actor_role && <div className="text-[11px] text-slate-400">{r.actor_role}</div>}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <Badge variant="outline" className="text-xs">{r.action}</Badge>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="text-slate-700">{r.entity_type || "—"}</div>
                                                        <div className="text-[11px] text-slate-400 font-mono">
                                                            {r.entity_id || "—"} · {r.module_scope}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2"><DiffChips diff={r.diff} /></td>
                                                    <td className="px-3 py-2">
                                                        {expanded === r.seq ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                    </td>
                                                </tr>
                                                {expanded === r.seq && <RowDetail entry={r} />}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                                {nextCursor && (
                                    <div className="p-4 text-center border-t">
                                        <Button size="sm" variant="outline" onClick={() => load({ append: true })} data-testid="load-more">
                                            Load more
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AppShell>
    );
}
